import type { BasketState } from '@wecasa/common/src/ducks/common/basket';
import type { HourlyBasketPrices, Promotion } from '@wecasa/common/src/types/commons';
import { formatDuration, formatPrice, formatPricePerHour } from '@wecasa/common/src/utils/formatting';

import { shouldFormatPromotionPerHour as shouldFormatPromotionPerHourUtils } from '@wecasa/customer/src/utils/basket';

import { translate } from '@wecasa/i18n';

import Text from '@wecasa/web-ui/src/components/base-text/variants/text';
import Title from '@wecasa/web-ui/src/components/base-text/variants/title';

import { CalculationWrapper, ChildcareCalculationLine, TotalLine } from '../../../../common/common';
import ReductionLine from '../../../../common/sections/prestations/reduction-line';

const t = translate('basket');

type HourlyCalculationPricesProps = {
  prices: HourlyBasketPrices;
  duration?: BasketState['duration'];
  promotion?: Promotion;
  showTaxCredit?: boolean;
};

const HourlyCalculationPrices = ({
  prices,
  duration,
  showTaxCredit = true,
  promotion,
}: HourlyCalculationPricesProps) => {
  const { hourlyPrice = 0, totalHourlyPrice = 0, promoValue, totalHourlyPriceAfterTax, totalPrice, giftValue } = prices;
  const durationInHours = duration ? formatDuration(duration) : undefined;
  const shouldFormatPromotionPerHour = shouldFormatPromotionPerHourUtils({ promotion, duration });

  return (
    <>
      <CalculationWrapper>
        {totalHourlyPrice > hourlyPrice && (
          <ChildcareCalculationLine>
            <Text.MDBold>{t('.total')}</Text.MDBold>
            <Text.MDBold>{formatPricePerHour(totalHourlyPrice)}</Text.MDBold>
          </ChildcareCalculationLine>
        )}
        {!!promoValue && (
          <ReductionLine
            title={t('.promo_code')}
            value={shouldFormatPromotionPerHour ? promotion!.amount : promoValue}
            shouldFormatPricePerHour={shouldFormatPromotionPerHour}
          />
        )}
        {!!giftValue && <ReductionLine title={t('.gift_card')} value={giftValue} />}
        {showTaxCredit && (
          <ChildcareCalculationLine>
            <Text>{t('.after_tax_credit')}</Text>
            <Text>{formatPricePerHour(totalHourlyPriceAfterTax)}</Text>
          </ChildcareCalculationLine>
        )}
      </CalculationWrapper>
      {durationInHours && (
        <TotalLine>
          <Title.MD>{formatPrice(totalPrice)}</Title.MD>
          <Text.MDBold>{t('.total_duration', { duration: durationInHours })}</Text.MDBold>
        </TotalLine>
      )}
    </>
  );
};

export default HourlyCalculationPrices;
